import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import { useNavigate } from "react-router-dom";
import type { SamplesFilters } from "@services/samples/list/type";
import { useGetSamples } from "@services/samples/list";
import { SamplesColumns } from "@typings/entities/samples/table";
import {
  prepareSamplesTable,
  type SampleItemListWithLabels,
} from "@utils/translator/samples";
import { config } from "@/config";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useExportSamples } from "@hooks/exports/useExportSamples";
import { usePermissions } from "@hooks/usePermissions";
import type { actionsType } from "@typings/components/table";

function TableView({ appliedFilters }: { appliedFilters: SamplesFilters }) {
  const { openModal, setDataModal } = usePage();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);

  const { handleExport, isLoading: isExporting } = useExportSamples();

  const {
    error,
    isLoading,
    data: samples,
  } = useGetSamples({
    ...appliedFilters,
    limit: rowsPerPage,
    page: page + 1,
  });

  const heads = SamplesColumns;

  const handleEdit = (sample: SampleItemListWithLabels) => {
    setDataModal({ id: sample.id });
    openModal("editSample");
  };

  const handleExportSamples = (filters: SamplesFilters) => async () => {
    handleExport(filters);
  };

  const handleView = (sample: SampleItemListWithLabels) => {
    navigate(`/samples/${sample.id}`);
  };

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  if (error) {
    toast.error("Erro ao carregar as configurações");
  }

  const preparedSamples = prepareSamplesTable(samples?.items || []);

  const canCreateSample = can("create", "samples");
  const canUpdateSample = can("update", "samples");

  const actions = ["view"] as actionsType;
  if (canUpdateSample) {
    actions.push("edit");
  }

  return (
    <div className="w-full">
      <div className="w-full mt-6 flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
        <TableHeader.Root>
          {canCreateSample && (
            <TableHeader.Button
              customVariant="secondary"
              startIcon={<AddIcon />}
              onClick={() => openModal("newSample")}
            >
              Nova Amostra
            </TableHeader.Button>
          )}
          <TableHeader.Button
            customVariant="dark"
            onClick={handleExportSamples(appliedFilters)}
            disabled={isExporting}
          >
            {isExporting ? "Exportando..." : "Exportar .csv"}
          </TableHeader.Button>
        </TableHeader.Root>

        <Table.Root isLoading={isLoading}>
          <Table.Head heads={heads} actions={true} />
          <Table.Body<SampleItemListWithLabels>
            rows={preparedSamples}
            columns={heads.map((head) => head.id)}
            actions={actions}
            onView={handleView}
            onEdit={handleEdit}
          />
          <Table.Footer
            colSpan={heads.length}
            count={samples?.meta.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Table.Root>
      </div>
    </div>
  );
}

export default TableView;
